import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import PostSocialLinks from "@/components/PostSocialLinks";
import PostShareLinks from "@/components/PostShareLinks";
import RelatedPostsBlock from "@/components/RelatedPostsBlock";
import { getPost } from "@/lib/api";
import { categoryStyle } from "@/lib/constants";
import { Phone, Globe, Building, ArrowLeft } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function PostDetailPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        setPost(null);
        setError(false);
        getPost(slug)
            .then(setPost)
            .catch(() => setError(true));
    }, [slug]);

    if (error) {
        return (
            <div className="px-6 py-32 text-center" data-testid="post-not-found">
                <h2 className="font-poppins text-3xl text-[#E63B26]">
                    Story not found
                </h2>
                <Link
                    to="/"
                    className="mt-4 inline-block font-poppins text-sm tracking-[0.18em] uppercase text-[#1B1B1B] hover:text-[#E63B26]"
                >
                    ← Back to home
                </Link>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="px-6 py-32 text-center font-sofia text-[#1B1B1B]/60">
                Loading…
            </div>
        );
    }

    return (
        <div data-testid="post-detail">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 px-4 md:px-8 lg:px-12 mt-10">
                <article className="lg:col-span-8 relative">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm font-poppins tracking-[0.18em] uppercase text-[#1B1B1B] hover:text-[#E63B26] mb-6"
                        data-testid="back-to-home"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back
                    </Link>

                    <div className="mt-6">
                        <span
                            className="badge-category"
                            style={categoryStyle(post.category)}
                        >
                            {post.category}
                        </span>
                    </div>
                    <p className="font-sofia text-sm text-[#1B1B1B]/70 mt-4">
                        {formatDate(post.date)}
                    </p>
                    <h1 className="font-poppins font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-2 leading-[1.1]">
                        {post.title}
                    </h1>
                    {post.sub_header && (
                        <p className="mt-4 italic font-sofia text-xl text-[#1B1B1B]/80">
                            {post.sub_header}
                        </p>
                    )}

                    <div className="mt-6">
                        <img
                            src={post.image_url}
                            alt={post.title}
                            className="w-full aspect-[3/2] object-cover bg-[#1B1B1B]"
                            data-testid="post-hero-image"
                        />
                    </div>

                    {post.thumbnails && post.thumbnails.length > 0 && (
                        <div className="grid grid-cols-3 gap-3 mt-3">
                            {post.thumbnails.slice(0, 3).map((t) => (
                                <img
                                    key={t}
                                    src={t}
                                    alt=""
                                    className="aspect-square object-cover w-full"
                                />
                            ))}
                        </div>
                    )}

                    <div className="mt-8 font-sofia text-sm leading-relaxed grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-8 border-l-4 border-[#F5C53D] pl-4 py-2">
                        <p className="font-bold text-[#1B1B1B]">
                            {post.contact_name}
                        </p>
                        <p className="text-[#1B1B1B]/85">
                            <Building className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                            {post.business}
                        </p>
                        <p className="text-[#1B1B1B]/85">
                            <Phone className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                            {post.phone}
                        </p>
                        <p className="text-[#E63B26]">
                            <Globe className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                            {post.website}
                        </p>
                    </div>

                    <PostSocialLinks post={post} />
                    <PostShareLinks title={post.title} />

                    <div className="mt-8 font-sofia text-[#1B1B1B]/90 text-lg leading-[1.75] whitespace-pre-line max-w-prose">
                        {post.body}
                    </div>

                    <RelatedPostsBlock
                        category={post.category}
                        excludeSlug={post.slug}
                    />

                    <div className="mt-12 text-center font-poppins text-[#1B1B1B]/40 tracking-[0.5em]">
                        ###
                    </div>
                </article>

                <div className="lg:col-span-4">
                    <Sidebar variant="with-ads" />
                </div>
            </div>
        </div>
    );
}
